import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CalendarDays, Clock, ArrowRight } from "lucide-react";
import { SEO } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("es-CO", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/blog`)
      .then((res) => setPosts(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="blog-page">
      <SEO
        title="Blog | Consejos de limpieza y hogar | Colors Limpieza Medellín"
        description="Consejos de limpieza profunda, desinfección de colchones, cuidado de muebles y tapicería de vehículos. Blog de Colors Limpieza Medellín."
      />

      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-widest text-brand">Blog</p>
            <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              Consejos para un hogar <span className="text-brand">más sano</span>
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
              Aprende a cuidar tus muebles, colchones y vehículo con tips de nuestros especialistas.
            </p>
          </Reveal>

          {loading ? (
            <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3 md:gap-8" data-testid="blog-loading">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse rounded-3xl bg-slate-100">
                  <div className="aspect-[16/9]" />
                  <div className="space-y-3 p-6">
                    <div className="h-3 w-16 rounded bg-slate-200" />
                    <div className="h-4 w-full rounded bg-slate-200" />
                    <div className="h-3 w-2/3 rounded bg-slate-200" />
                  </div>
                </div>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <p className="mt-14 text-center text-slate-500" data-testid="blog-empty">
              Pronto publicaremos nuevos artículos.
            </p>
          ) : (
            <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3 md:gap-8">
              {posts.map((p, i) => (
                <Reveal key={p.slug} delay={i * 0.08}>
                  <Link
                    to={`/blog/${p.slug}`}
                    className="group flex h-full flex-col overflow-hidden rounded-3xl bg-white shadow-[0_10px_30px_-10px_rgba(0,183,205,0.12)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_-15px_rgba(15,23,42,0.15)]"
                    data-testid={`blog-card-${p.slug}`}
                  >
                    <div className="overflow-hidden">
                      <img
                        src={p.image}
                        alt={p.title}
                        className="aspect-[16/9] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <div className="flex items-center gap-3">
                        <span className="rounded-full bg-sunset-light px-3 py-1 text-xs font-semibold text-sunset-dark">{p.category}</span>
                        <span className="flex items-center gap-1 text-xs text-slate-400">
                          <Clock size={12} /> {p.read_time}
                        </span>
                      </div>
                      <h2 className="mt-3 font-display text-base font-semibold leading-snug text-slate-900 sm:text-lg">
                        {p.title}
                      </h2>
                      <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{p.excerpt}</p>
                      <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4">
                        <span className="flex items-center gap-1.5 text-xs text-slate-400">
                          <CalendarDays size={13} /> {formatDate(p.date)}
                        </span>
                        <span className="inline-flex items-center gap-1 text-sm font-semibold text-brand group-hover:text-brand-dark">
                          Leer <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
